import { useCallback, useEffect, useId, useRef, useState } from "react";
import { getActiveSliders, ISlider } from "../services/slider";
import { useLanguage } from "../context/LanguageContext";
import "../styles/style.css";

const AUTOPLAY_MS = 5000;

const sliderHref = (linkUrl: string) =>
  linkUrl.match(/^https?:\/\//) ? linkUrl : `https://${linkUrl}`;

function SlideImage({ slider, index, className, onLoad }: {
  slider: ISlider;
  index: number;
  className: string;
  onLoad?: () => void;
}) {
  const img = (
    <img
      className={`${className}${slider.linkUrl ? " main-carousel-img--clickable" : ""}`}
      src={slider.imageUrl}
      alt={slider.title || `Slide ${index + 1}`}
      onLoad={onLoad}
      draggable={false}
    />
  );
  return slider.linkUrl ? (
    <a href={sliderHref(slider.linkUrl)} target="_blank" rel="noopener noreferrer" className="main-carousel-link">
      {img}
    </a>
  ) : img;
}

/* ---------------- Masaüstü: yan yana 2 slayt, yuvarlak oklar, durdur/oynat ---------------- */

function DesktopCarousel({ sliders }: { sliders: ISlider[] }) {
  const { t } = useLanguage();
  const trackRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const scrollable = sliders.length > 1;

  const updateEdges = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 2);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 2);
  }, []);

  // Bir slayt genişliği + aradaki boşluk kadar kaydır; uçlarda başa/sona sar
  const go = useCallback((dir: 1 | -1) => {
    const el = trackRef.current;
    const first = el?.firstElementChild as HTMLElement | null;
    if (!el || !first) return;
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
    const step = first.offsetWidth + gap;
    const maxLeft = el.scrollWidth - el.clientWidth;
    let target = el.scrollLeft + dir * step;
    if (dir === 1 && el.scrollLeft >= maxLeft - 2) target = 0;
    if (dir === -1 && el.scrollLeft <= 2) target = maxLeft;
    el.scrollTo({ left: target, behavior: "smooth" });
  }, []);

  useEffect(() => {
    updateEdges();
    window.addEventListener("resize", updateEdges);
    return () => window.removeEventListener("resize", updateEdges);
  }, [updateEdges, sliders.length]);

  useEffect(() => {
    if (!scrollable || paused || hovered) return;
    const id = window.setInterval(() => {
      if (!document.hidden) go(1);
    }, AUTOPLAY_MS);
    return () => window.clearInterval(id);
  }, [scrollable, paused, hovered, go]);

  // Kaydırılacak kadar slayt yoksa (ör. 2 slayt yan yana sığıyorsa) okları gösterme
  const showControls = scrollable && !(atStart && atEnd);

  return (
    <div className="container">
      <div
        className={`desktop-carousel${sliders.length === 1 ? " desktop-carousel--single" : ""}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="desktop-carousel__track" ref={trackRef} onScroll={updateEdges}>
          {sliders.map((slider, i) => (
            <div key={slider.id} className="desktop-carousel__slide">
              <SlideImage slider={slider} index={i} className="desktop-carousel__img" />
            </div>
          ))}
        </div>

        {showControls && (
          <>
            <button
              type="button"
              className="desktop-carousel__arrow desktop-carousel__arrow--prev"
              onClick={() => go(-1)}
              aria-label={t("carousel.prev")}
            >
              <i className="bi bi-arrow-left"></i>
            </button>
            <button
              type="button"
              className="desktop-carousel__arrow desktop-carousel__arrow--next"
              onClick={() => go(1)}
              aria-label={t("carousel.next")}
            >
              <i className="bi bi-arrow-right"></i>
            </button>
          </>
        )}
      </div>

      {showControls && (
        <div className="d-flex justify-content-end">
          <button
            type="button"
            className="desktop-carousel__pause"
            onClick={() => setPaused((p) => !p)}
            aria-label={paused ? t("carousel.play") : t("carousel.pause")}
            aria-pressed={paused}
          >
            <i className={`bi ${paused ? "bi-play-fill" : "bi-pause-fill"}`}></i>
          </button>
        </div>
      )}
    </div>
  );
}

/* ---------------- Mobil: tek slayt, Bootstrap carousel ---------------- */

function MobileCarousel({ sliders }: { sliders: ISlider[] }) {
  // Dashboard bu bileşeni hem mobil hem masaüstü düzeninde render ediyor; sabit id
  // ikisinde aynı olunca oklar sayfadaki ilk (gizli) carousel'i kaydırıyordu.
  const { t } = useLanguage();
  const carouselId = `carousel-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const [firstLoaded, setFirstLoaded] = useState(false);

  return (
    <div className="main-carousel-wrap">
      <div className="main-carousel-inner">
        {!firstLoaded && <div className="skeleton main-carousel-skeleton" />}
        <div
          id={carouselId}
          className={`carousel slide${firstLoaded ? "" : " d-none"}`}
          data-bs-ride="carousel"
        >
          {sliders.length > 1 && (
            <div className="carousel-indicators">
              {sliders.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-bs-target={`#${carouselId}`}
                  data-bs-slide-to={i}
                  className={i === 0 ? "active" : ""}
                  aria-current={i === 0 ? "true" : undefined}
                  aria-label={`Slide ${i + 1}`}
                />
              ))}
            </div>
          )}

          <div className="carousel-inner main-carousel-track">
            {sliders.map((slider, i) => (
              <div key={slider.id} className={`carousel-item${i === 0 ? " active" : ""}`}>
                <SlideImage
                  slider={slider}
                  index={i}
                  className="main-carousel-img"
                  onLoad={i === 0 ? () => setFirstLoaded(true) : undefined}
                />
              </div>
            ))}
          </div>

          {sliders.length > 1 && (
            <>
              <button className="carousel-control-prev" type="button" data-bs-target={`#${carouselId}`} data-bs-slide="prev">
                <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                <span className="visually-hidden">{t("carousel.prev")}</span>
              </button>
              <button className="carousel-control-next" type="button" data-bs-target={`#${carouselId}`} data-bs-slide="next">
                <span className="carousel-control-next-icon" aria-hidden="true"></span>
                <span className="visually-hidden">{t("carousel.next")}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function MainCarousel({ variant = "mobile" }: { variant?: "mobile" | "desktop" }) {
  const [sliders, setSliders] = useState<ISlider[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getActiveSliders()
      .then((res) => {
        const active = res.data
          .filter((s) => s.isEnabled)
          .sort((a, b) => a.displayOrder - b.displayOrder);
        setSliders(active);
      })
      .catch(() => setSliders([]))
      .finally(() => setLoading(false));
  }, []);

  if (variant === "desktop") {
    if (loading) {
      return (
        <div className="container">
          <div className="desktop-carousel">
            <div className="desktop-carousel__track">
              <div className="desktop-carousel__slide"><div className="skeleton desktop-carousel__img" /></div>
              <div className="desktop-carousel__slide"><div className="skeleton desktop-carousel__img" /></div>
            </div>
          </div>
        </div>
      );
    }
    return sliders.length > 0 ? <DesktopCarousel sliders={sliders} /> : null;
  }

  if (loading || sliders.length === 0) {
    return (
      <div className="main-carousel-wrap">
        <div className="main-carousel-inner">
          <div className="skeleton main-carousel-skeleton" />
        </div>
      </div>
    );
  }

  return <MobileCarousel sliders={sliders} />;
}

export default MainCarousel;
